import { Box, CircularProgress, FormControlLabel, MenuItem, Switch, TextField, Typography } from "@mui/material";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTenantService, TenantLifecycleStatus, toTenantLifecycleStatus, tenantLifecycleStatusToNumber } from "./tenantService";
import { useHistory } from "react-router-dom";
import { useState } from "react";

type Props = {
  tenant: any;
};

export const EditTenantForm: React.FC<Props> = ({ tenant }) => {

  const isAdd = !tenant;

  const [includeTypes, setIncludeTypes] = useState<boolean>(true);

  const tenantService = useTenantService();
  const history = useHistory();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
  });
  const formOptions = {
    defaultValues: {
      name: tenant?.name ?? "",
      description: tenant?.description ?? "",
      notes: tenant?.notes ?? "",
      lifecycleStatus: toTenantLifecycleStatus(tenant?.lifecycleStatus),
      subscriptionPriceSite: tenant?.subscriptionPrices?.site ?? 13000,
      subscriptionPriceEntity: tenant?.subscriptionPrices?.entity ?? 2000,
    },
    resolver: yupResolver(validationSchema),
  };

  const { register, handleSubmit, formState, setValue } = useForm(formOptions);
  const { errors, isSubmitting } = formState;

  const onSubmit = async (values: any) => {
    const { subscriptionPriceSite, subscriptionPriceEntity, lifecycleStatus, ...rest } = values;
    const formData = {
      ...rest,
      lifecycleStatus: tenantLifecycleStatusToNumber(lifecycleStatus),
      subscriptionPrices: {
        site: Number(subscriptionPriceSite),
        entity: Number(subscriptionPriceEntity),
      },
    };
    if(isAdd) {
      await tenantService.post({...formData, createdOn: new Date().toUTCString()}, includeTypes);
    } else {
      await tenantService.put(tenant.id, {...tenant, ...formData});
    }
    history.push('/tenants')
  };

  return (
    <Box
      component="form"
      sx={{
        "& .MuiTextField-root": { m: "1rem" },
      }}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="left"
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div>
        <TextField
          label="Name"
          variant="outlined"
          type="text"
          fullWidth
          {...register("name")}
        />
        <div>{errors.name?.message}</div>
      </div>
      <div>
        <TextField
          label="Description"
          variant="outlined"
          type="text"
          fullWidth
          multiline
          rows={3}
          {...register("description")}
        />
      </div>
      <div>
        <TextField
          label="Notes"
          variant="outlined"
          type="text"
          fullWidth
          multiline
          rows={3}
          {...register("notes")}
        />
      </div>
      <div>
        <TextField
          {...register("lifecycleStatus")}
          fullWidth
          select
          onChange={(e) => setValue("lifecycleStatus", e.target.value)}
          label="Lifecycle Status"
          defaultValue={formOptions.defaultValues.lifecycleStatus}
        >
          {Object.values(TenantLifecycleStatus).map((status) => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </TextField>
      </div>
      <Typography variant="h6" sx={{ mt: 2, ml: 2 }}>Abonnementspriser</Typography>
      <div>
        <TextField
          label="Standard sitepris (NOK)"
          variant="outlined"
          type="number"
          fullWidth
          {...register("subscriptionPriceSite")}
        />
      </div>
      <div>
        <TextField
          label="Standard entitypris (NOK)"
          variant="outlined"
          type="number"
          fullWidth
          {...register("subscriptionPriceEntity")}
        />
      </div>
      {isAdd && (
        <FormControlLabel
          control={
            <Switch
              checked={includeTypes}
              onChange={(_, v) => setIncludeTypes(v)}
              color="primary"
            />
          }
          label={"Include Entity Types"}
        />
      )}
      <button disabled={isSubmitting} className="btn btn-primary">
        {isSubmitting ? <CircularProgress style={{ color: "#fff" }} /> : "Save"}
      </button>
    </Box>
  );
};
